import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Lightbulb, Scale } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { DECISION_STATUS, ITEM_KIND, RISK_STATUS, SEVERITY, fmtDate, fmtEUR } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader } from "../../components/common";
import { ActionButtons, OpsKpis, useProjectPicker } from "../../components/ops";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const TABS = [
  { id: "riesgos", label: "Riesgos", icon: AlertTriangle },
  { id: "incidencias", label: "Incidencias", icon: AlertTriangle },
  { id: "decisiones", label: "Decisiones", icon: Scale },
];

function CreateItemDialog({ pid, kind, users }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/proyectos/${pid}/ops/riesgos`, { ...f, kind })).data,
    onSuccess: () => { toast.success(kind === "risk" ? "Riesgo registrado" : "Incidencia registrada"); setOpen(false); setF({}); qc.invalidateQueries({ queryKey: ["ops-risks", pid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  const isRisk = kind === "risk";
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid={`create-${kind}-open`}>
        {isRisk ? "Nuevo riesgo" : "Nueva incidencia"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`create-${kind}-dialog`}>
          <DialogHeader><DialogTitle>{isRisk ? "Nuevo riesgo (evento posible)" : "Nueva incidencia (problema materializado)"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="create-item-title" /></div>
            <div><Label>Descripción</Label><Textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="create-item-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Categoría</Label>
                <Select value={f.category || "tecnico"} onValueChange={(v) => setF({ ...f, category: v })}>
                  <SelectTrigger data-testid="create-item-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{["planning", "presupuesto", "contrato", "tecnico", "permisos", "calidad", "seguridad", "externo"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Severidad</Label>
                <Select value={f.severity || "media"} onValueChange={(v) => setF({ ...f, severity: v })}>
                  <SelectTrigger data-testid="create-item-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{["baja", "media", "alta", "critica"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Causa</Label><Input value={f.cause || ""} onChange={(e) => setF({ ...f, cause: e.target.value })} data-testid="create-item-cause" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Impacto económico (€)</Label><Input type="number" value={f.impact_cost || ""} onChange={(e) => setF({ ...f, impact_cost: Number(e.target.value) })} data-testid="create-item-cost" /></div>
              <div><Label>Impacto en plazo (días)</Label><Input type="number" value={f.impact_days || ""} onChange={(e) => setF({ ...f, impact_days: Number(e.target.value) })} data-testid="create-item-days" /></div>
            </div>
            <div><Label>Código de coste</Label><Input value={f.cost_code || ""} onChange={(e) => setF({ ...f, cost_code: e.target.value })} placeholder="05.1" data-testid="create-item-costcode" /></div>
            <div><Label>Responsable</Label>
              <Select value={f.responsible_id || ""} onValueChange={(v) => setF({ ...f, responsible_id: v })}>
                <SelectTrigger data-testid="create-item-responsible"><SelectValue placeholder="PM del proyecto" /></SelectTrigger>
                <SelectContent>{(users.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="create-item-submit" disabled={!f.title || mut.isPending} onClick={() => mut.mutate()}>Registrar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ItemsTable({ pid, kind }) {
  const users = useUsers();
  const { data: items } = useQuery({
    queryKey: ["ops-risks", pid, kind],
    queryFn: async () => (await api.get(`/proyectos/${pid}/ops/riesgos`, { params: { kind } })).data,
  });
  if (!items) return null;
  if (!items.length) return <EmptyState title={kind === "risk" ? "Sin riesgos" : "Sin incidencias"} description="Registra el primero con el botón superior." testid={`empty-${kind}`} />;
  return (
    <Table data-testid={`${kind}-table`}>
      <TableHeader>
        <TableRow>
          <TableHead>Tipo</TableHead><TableHead>Título</TableHead><TableHead>Estado</TableHead><TableHead>Severidad</TableHead>
          <TableHead>Causa</TableHead><TableHead>Impacto</TableHead><TableHead>Responsable</TableHead><TableHead>Evidencia</TableHead><TableHead>Acciones</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((i) => (
          <TableRow key={i.id} data-testid={`risk-row-${i.id}`}>
            <TableCell><Chip map={ITEM_KIND} value={i.kind} testid={`risk-kind-${i.id}`} /></TableCell>
            <TableCell className="max-w-64"><p className="font-medium text-slate-800">{i.title}</p>
              <p className="text-xs text-slate-400">{i.id} · {fmtDate(i.created_at)}{i.origin?.startsWith("automatizacion") ? " · creado por automatización" : ""}</p></TableCell>
            <TableCell><Chip map={RISK_STATUS} value={i.status} testid={`risk-status-${i.id}`} /></TableCell>
            <TableCell><Chip map={SEVERITY} value={i.severity} testid={`risk-severity-${i.id}`} /></TableCell>
            <TableCell className="max-w-48 text-xs text-slate-500">{i.cause || "—"}</TableCell>
            <TableCell className="text-xs">{i.impact_cost ? fmtEUR(i.impact_cost) : "—"}{i.impact_days ? ` · ${i.impact_days} d` : ""}</TableCell>
            <TableCell className="text-xs">{userName(users.map, i.responsible_id)}</TableCell>
            <TableCell className="max-w-40 truncate text-xs text-slate-500" title={i.evidence || ""}>{i.evidence || (i.status === "cerrado" ? "—" : "pendiente")}</TableCell>
            <TableCell><ActionButtons item={i} endpoint={`/proyectos/${pid}/ops/riesgos`} invalidate={[["ops-risks", pid]]} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function DecisionesTab({ pid, users }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const { data: items } = useQuery({
    queryKey: ["ops-decisions", pid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/ops/decisiones`)).data,
  });
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/proyectos/${pid}/ops/decisiones`, f)).data,
    onSuccess: () => { toast.success("Decisión propuesta"); setOpen(false); setF({}); qc.invalidateQueries({ queryKey: ["ops-decisions", pid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  return (
    <div className="space-y-3" data-testid="decisiones-tab">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setOpen(true)} data-testid="create-decision-open">Nueva decisión</Button>
      </div>
      {!items?.length ? <EmptyState title="Sin decisiones" description="Propón la primera decisión." /> : (
        <Table data-testid="decisiones-table">
          <TableHeader><TableRow>
            <TableHead>Título</TableHead><TableHead>Estado</TableHead><TableHead>Propone</TableHead>
            <TableHead>Decide</TableHead><TableHead>Evidencia impl.</TableHead><TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((d) => (
              <TableRow key={d.id} data-testid={`decision-row-${d.id}`}>
                <TableCell className="max-w-64"><p className="font-medium text-slate-800">{d.title}</p>
                  <p className="text-xs text-slate-400">{d.id}</p></TableCell>
                <TableCell><Chip map={DECISION_STATUS} value={d.status} testid={`decision-status-${d.id}`} /></TableCell>
                <TableCell className="text-xs">{userName(users.map, d.proposed_by)}</TableCell>
                <TableCell className="text-xs">{d.decided_by ? userName(users.map, d.decided_by) : "—"}</TableCell>
                <TableCell className="max-w-40 truncate text-xs text-slate-500">{d.implementation_evidence || "—"}</TableCell>
                <TableCell><ActionButtons item={d} endpoint={`/proyectos/${pid}/ops/decisiones`} invalidate={[["ops-decisions", pid]]} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-decision-dialog">
          <DialogHeader><DialogTitle>Nueva decisión</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="create-decision-title" /></div>
            <div><Label>Descripción / opciones</Label><Textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="create-decision-desc" /></div>
            <Button data-testid="create-decision-submit" disabled={!f.title || mut.isPending} onClick={() => mut.mutate()}>Proponer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Riesgos() {
  const [sp, setSp] = useSearchParams();
  const [pid, picker] = useProjectPicker();
  const tab = sp.get("tab") || "riesgos";
  const users = useUsers();
  const { user } = useAuth();
  const canWrite = user && !(user.roles?.length === 1 && user.roles[0] === "VIEWER");
  return (
    <div className="space-y-4" data-testid="riesgos-page">
      <PageHeader title="Riesgos, incidencias y decisiones"
        subtitle="Risk = evento posible · Issue = problema materializado. Ninguno se cierra sin resultado, evidencia y revisión."
        actions={picker} testid="riesgos-title" />
      <OpsKpis pid={pid} />
      <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-px">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setSp((p) => { p.set("tab", t.id); return p; })}
            className={`flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors ${tab === t.id ? "bg-white text-blue-800 shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-800"}`}
            data-testid={`tab-${t.id}`}>
            <t.icon className="h-3.5 w-3.5" />{t.label}
          </button>
        ))}
      </div>
      {tab !== "decisiones" && canWrite && (
        <div className="flex justify-end gap-2">
          <CreateItemDialog pid={pid} kind="risk" users={users} />
          <CreateItemDialog pid={pid} kind="issue" users={users} />
        </div>
      )}
      {tab === "riesgos" && <ItemsTable pid={pid} kind="risk" />}
      {tab === "incidencias" && <ItemsTable pid={pid} kind="issue" />}
      {tab === "decisiones" && <DecisionesTab pid={pid} users={users} />}
    </div>
  );
}
